import { useState } from 'react'

type ScoreCellProps = {
  value: number | null
  maxValue: number
  onChange: (value: number | null) => void
  hasError: boolean
  isEmpty: boolean
  inputRef?: React.RefCallback<HTMLInputElement>
  onAdvance?: (value: number) => void
  onRetreat?: () => void
}

export function ScoreCell({ value, maxValue, onChange, hasError, isEmpty, inputRef, onAdvance, onRetreat }: ScoreCellProps) {
  const [inputValue, setInputValue] = useState(value?.toString() ?? '')
  const [prevValue, setPrevValue] = useState(value)

  // Sync local state when external value changes (update during render pattern)
  if (prevValue !== value) {
    setPrevValue(value)
    setInputValue(value?.toString() ?? '')
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value
    setInputValue(raw)

    if (raw === '') {
      onChange(null)
      return
    }

    const num = parseInt(raw, 10)
    if (!Number.isNaN(num) && num >= 1 && num <= maxValue) {
      onChange(num)
      if (num * 10 > maxValue) {
        onAdvance?.(num)
      }
    } else {
      onChange(null)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && inputValue === '') {
      e.preventDefault()
      onRetreat?.()
    }
  }

  const handleBlur = () => {
    // Reset display to actual value on blur
    setInputValue(value?.toString() ?? '')
  }

  let bgClass = 'bg-white'
  let borderClass = 'border-black/20'

  if (hasError) {
    bgClass = 'bg-red-100'
    borderClass = 'border-red-500'
  } else if (isEmpty) {
    bgClass = 'bg-orange-100'
    borderClass = 'border-orange-400'
  }

  return (
    <input
      ref={inputRef}
      type="text"
      inputMode="numeric"
      value={inputValue}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onFocus={(e) => e.target.select()}
      onBlur={handleBlur}
      className={`w-12 h-12 md:w-16 md:h-16 text-center text-base md:text-xl border rounded ${bgClass} ${borderClass} focus:outline-none focus:border-black`}
    />
  )
}
